import React, { useState } from 'react'
import styled from 'styled-components'
import Colors from '../commons/Colors'
import { IoMdClose } from 'react-icons/io'
import RoundTagComponent from '../commons/components/RoundTagComponent'
import { getTagColor } from './NotificationCardComponent'

const NotificationDetailsModalComponent = ({
    isNotificationNew,
    isVisible,
    notification,
    onClose,
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)

    const sortedFiles = notification?.files?.sort((a, b) =>
        a.path.localeCompare(b.path)
    )
    const sortedImages = sortedFiles?.filter(file =>
        /\.(jpg|jpeg|png|gif)$/i.test(file.path)
    )
    const sortedPDFs = sortedFiles?.filter(file => /\.pdf$/i.test(file.path))

    const handleNextImage = () => {
        setCurrentImageIndex(prevIndex => (prevIndex + 1) % sortedImages.length)
    }

    const handlePrevImage = () => {
        setCurrentImageIndex(
            prevIndex =>
                (prevIndex - 1 + sortedImages.length) % sortedImages.length
        )
    }

    if (!isVisible) return null

    return (
        <ModalOverlay>
            <ModalContainer>
                <Header>
                    {isNotificationNew && (
                        <RoundTagComponent
                            text={'Novo'}
                            color={Colors.heavyGreen}
                        />
                    )}

                    <RoundTagComponent
                        text={notification?.source.name}
                        color={getTagColor(notification?.source.name)}
                    />
                    {notification?.version && (
                        <ReleaseInformationText>
                            {`Disponível na versão ${notification?.version}`}
                        </ReleaseInformationText>
                    )}
                    <CloseButton
                        data-testid="close-button-notification-details"
                        onClick={onClose}
                    >
                        <IoMdClose size={20} />
                    </CloseButton>
                </Header>
                <Title>{notification?.name}</Title>
                <Description>{notification?.description}</Description>
                {sortedPDFs?.length > 0 && (
                    <PDFList>
                        {sortedPDFs.map(file => (
                            <PDFLink
                                key={file.id}
                                href={file.path}
                                target="_blank"
                                rel="pdf"
                            >
                                {file.path.split('/').pop()}
                            </PDFLink>
                        ))}
                    </PDFList>
                )}
                {sortedImages?.length > 0 && (
                    <ImageContainer>
                        <ImageLink
                            href={sortedImages[currentImageIndex].path}
                            target="_blank"
                            rel="image"
                        >
                            <Image
                                src={sortedImages[currentImageIndex].path}
                                alt="Notification Image"
                            />
                        </ImageLink>
                        {sortedImages.length > 1 && (
                            <ImageFooter>
                                <NavButton
                                    direction={'left'}
                                    onClick={handlePrevImage}
                                >
                                    {'<'}
                                </NavButton>
                                <ImageIndicator>
                                    {currentImageIndex + 1}/
                                    {sortedImages.length}
                                </ImageIndicator>
                                <NavButton
                                    direction={'right'}
                                    onClick={handleNextImage}
                                >
                                    {'>'}
                                </NavButton>
                            </ImageFooter>
                        )}
                    </ImageContainer>
                )}
            </ModalContainer>
        </ModalOverlay>
    )
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`

const ModalContainer = styled.div`
    background-color: ${Colors.white};
    width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    border-radius: 8px;
    position: relative;
    padding: 20px;
`

const Header = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding-top: 5px; /* Optional: Add padding for better spacing */
`

const ReleaseInformationText = styled.span`
    position: absolute;
    right: 45px; /* Keep the close button at the far right */
    font-family: Roboto;
    font-size: 9px;
    color: ${Colors.lightBlueGreyTwo};
`

const CloseButton = styled.button`
    position: absolute;
    right: 8px; /* Keep the close button at the far right */
    transform: translateY(-50%); /* Adjust for perfect vertical alignment */
    background: none;
    border: none;
    cursor: pointer;
`

const TagsContainer = styled.div`
    display: flex;
    align-items: center;
    padding-bottom: 2px;
`

const Title = styled.h1`
    font-family: Roboto;
    font-weight: bold;
    font-size: 14px;
    color: ${Colors.brownishGrey};
    margin: 20px 0 10px;
`

const Description = styled.p`
    font-family: Roboto;
    font-size: 12px;
    line-height: 20px;
    color: ${Colors.brownishGrey};
    margin-bottom: 20px;
`

const PDFList = styled.div`
    margin-bottom: 20px;
`

const PDFLink = styled.a`
    display: block;
    font-size: 14px;
    color: ${Colors.orange};
    text-decoration: none;
    margin-bottom: 5px;

    &:hover {
        text-decoration: underline;
    }
`

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ImageLink = styled.a`
    display: block;
    max-width: 100%;
    margin-bottom: 10px;
    text-decoration: none;

    &:hover {
        opacity: 0.8; /* Add a hover effect for better user experience */
    }
`

const Image = styled.img`
    width: 100%;
    max-height: 300px;
    object-fit: cover;
    border-radius: 5px; /* Optional: Add rounded corners */
`

const ImageFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`

const NavButton = styled.button`
    background-color: transparent;
    font-family: Roboto;
    font-size: 16px;
    color: ${Colors.brownishGrey};
    border: none;
    margin: 0px 20px 0px 20px;
    cursor: pointer;

    &:hover {
        background-color: ${Colors.orange};
        color: ${Colors.white};
    }
`

const ImageIndicator = styled.span`
    font-family: Roboto;
    font-weight: bold;
    font-size: 12px;
    color: ${Colors.brownishGrey};
`

export default NotificationDetailsModalComponent
